import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth, roleHome } from "@/lib/auth";
import { ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import SavvyLogo from "@/components/SavvyLogo";
import { toast } from "sonner";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        if (!alive) return;
        setInfo(data);
        setName(data.name || "");
      } catch (e) {
        if (!alive) return;
        setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, { password, name });
      toast.success("Account activated");
      await refresh();
      navigate(roleHome(data.user?.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-stone-50" data-testid="invite-accept-page">
      <div className="hidden lg:block relative">
        <img
          alt=""
          src="https://images.pexels.com/photos/17815725/pexels-photo-17815725.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-950/60" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center">
            <SavvyLogo variant="dark" size={44} />
          </div>
          <div>
            <div className="overline text-brand-300 mb-3">Customer invitation</div>
            <h2 className="font-display text-4xl font-black leading-tight max-w-md">
              Activate your buyer portal.
            </h2>
            <div className="mt-6 border-l-2 border-brand-500 pl-4 max-w-md text-sm text-stone-200">
              Your builder invited you to track progress, payments, and tickets in one
              place. Finish activating your account to get started.
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="invite-form-container">
          <div className="overline text-brand-600 mb-3">Activation</div>
          <h1 className="font-display text-4xl font-black tracking-tight">
            Set your password.
          </h1>
          {loading ? (
            <p className="text-stone-500 mt-6 overline">Loading invite…</p>
          ) : info ? (
            <>
              <p className="text-stone-600 mt-2 text-base">
                Welcome, <span className="font-bold text-stone-950">{info.name || info.email}</span>.
                {info.company_name && (
                  <>
                    {" "}You're joining the portal for{" "}
                    <span className="font-bold text-stone-950">{info.company_name}</span>.
                  </>
                )}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="overline text-stone-500 block mb-2">Email</label>
                  <input
                    data-testid="invite-email-input"
                    value={info.email}
                    disabled
                    className="w-full border border-stone-200 bg-stone-100 text-stone-500 px-4 py-3 text-base font-mono"
                  />
                </div>
                <div>
                  <label className="overline text-stone-500 block mb-2">Full name</label>
                  <input
                    data-testid="invite-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full border border-stone-300 bg-white px-4 py-3 text-base focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="overline text-stone-500 block mb-2">New password</label>
                  <input
                    data-testid="invite-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full border border-stone-300 bg-white px-4 py-3 text-base focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="overline text-stone-500 block mb-2">Confirm password</label>
                  <input
                    data-testid="invite-confirm-input"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="w-full border border-stone-300 bg-white px-4 py-3 text-base focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none"
                  />
                </div>
                {error && (
                  <div className="flex items-start gap-2 border border-red-400 bg-red-50 text-red-800 p-3 text-sm" data-testid="invite-error">
                    <AlertTriangle size={16} className="mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <button
                  data-testid="invite-submit-button"
                  type="submit"
                  disabled={busy}
                  className="w-full bg-brand-500 hover:bg-brand-600 disabled:bg-stone-300 text-white font-bold uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 transition-colors"
                >
                  {busy ? "Activating…" : "Activate account"} <ArrowRight size={16} />
                </button>
              </form>
              <div className="mt-4 text-xs text-stone-500 flex items-center gap-1">
                <CheckCircle2 size={12} /> Your invite is valid for 14 days.
              </div>
              <div className="mt-8 flex items-center gap-3" data-testid="invite-powered-by">
                <div className="text-[10px] tracking-[0.25em] text-stone-400 uppercase">
                  Powered by
                </div>
                <img
                  src="https://customer-assets.emergentagent.com/job_construct-hub-357/artifacts/aps478ky_logo.png"
                  alt="SavvySpark Solutions Private Limited"
                  className="h-11 w-auto logo-blend-light"
                  data-testid="invite-savvyspark-logo"
                />
              </div>
            </>
          ) : (
            <div className="mt-6 border border-red-400 bg-red-50 text-red-800 p-4" data-testid="invite-error">
              {error || "Invite not found or has expired."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
